import AnimationClip from '../AnimationClip';
import GameObject from '../../../GameObject/GameObject';
import { Tween, Easing } from '@tweenjs/tween.js';
import { MathHelper } from '../../../Utility/MathHelper';
import {
    PlayAnimation,
    AwaitIterators,
    ConcatIterators,
} from '../AnimationHelper';
import { ShowTurnipReward } from './ShowTurnipReward';
import { PlaySound } from '../../Audio/AudioHelper';
import Game from '../../../Game';
import { AudioTrackType } from '../../Audio/AudioClip';
import AnimationFrame from '../../../Component/AnimationFrame';
import { delay } from '../../../Utility/Task/TaskQueue';

export interface FlyingTurnipParams {
    finalParent: GameObject;
    parentScale: number;
    getTargetPosition: (parentScale: number) => { x: number; y: number };
    rewardObject: GameObject;
    shinyObject: AnimationFrame;
    isHighReward: boolean;
}

export const finalScale = 0.85;

export class FlyingTurnip extends AnimationClip<FlyingTurnipParams> {
    public *execute(
        gameObject: GameObject,
        meta: FlyingTurnipParams
    ): IterableIterator<number> {

        const flyDurationMs = 1000;

        const dynamicParentScale = { parentScale: meta.parentScale };

        const dynamicStartPosition = gameObject.container.position.clone();
        const dynamicTargetPosition = meta.getTargetPosition.call(null, dynamicParentScale.parentScale);

        const startPositionInFinalParent = (() => {
            const initialParent = gameObject.parent;
            meta.finalParent.addChild(gameObject);
            const result = gameObject.container.position.clone();
            initialParent.addChild(gameObject);
            return result;
        })();

        // somewhere mid animation, after it's not overlapping with dirt piles
        // reparent so that all turnips have same parent and can have proper z-order
        // at the same time, bring to front
        const reparentTurnip = () => {
            meta.finalParent.addChild(gameObject);

            // Reparenting also means change of scale
            dynamicParentScale.parentScale = 1;

            dynamicStartPosition.x = startPositionInFinalParent.x;
            dynamicStartPosition.y = startPositionInFinalParent.y;

            scaleTweens.updateFinalScaleTween();

            const newTargetPosition = meta.getTargetPosition.call(null, dynamicParentScale.parentScale);
            dynamicTargetPosition.x = newTargetPosition.x;
            dynamicTargetPosition.y = newTargetPosition.y;
        };

        // Get tweens

        const rotationTweens = this.getRotationTweens(gameObject.container);
        const scaleTweens = this.getScaleTweens(
            gameObject.container,
            flyDurationMs,
            dynamicParentScale
        );
        const positionTweens = this.getPositionTweens(
            gameObject.container.position,
            dynamicStartPosition,
            dynamicTargetPosition,
            dynamicParentScale,
            flyDurationMs
        );

        // Start animation

        yield* rotationTweens.prepare;

        yield* AwaitIterators([
            (function*() {
                yield* delay(100);
                PlaySound('pick-turnip');
            }()),
            rotationTweens.yoyo,
            scaleTweens.yoyo,
        ]);
        yield* AwaitIterators([
            rotationTweens.reset,
            positionTweens.fly,
            scaleTweens.final,
            ConcatIterators([
                delay(flyDurationMs * 0.1),
                PlayAnimation(
                    new ShowTurnipReward(meta.rewardObject, {
                        rewardObject: meta.rewardObject,
                        shinyObject: meta.shinyObject,
                        isHighReward: meta.isHighReward,
                    })
                )
            ]),
            ConcatIterators([
                delay(flyDurationMs * 0.75),
                (function*() { reparentTurnip(); })()
            ])
        ]);
    }

    private getRotationTweens(turnipContainer: PIXI.Container) {
        const prepare = this.getTween(turnipContainer).to(
            { rotation: 0.2 },
            100
        );

        const yoyo = this.getTween(turnipContainer)
            .to({ rotation: -0.2 }, 200)
            .yoyo(true)
            .repeat(4);

        const reset = this.getTween(turnipContainer).to(
            { rotation: 0 },
            100
        );

        return {
            prepare: this.generateTween(prepare),
            yoyo: this.generateTween(yoyo),
            reset: this.generateTween(reset),
        };
    }

    private getScaleTweens(
        turnipContainer: PIXI.Container,
        flyDurationMs: number,
        dynamicData: { parentScale: number },
    ) {
        const yoyo = this.getTween(turnipContainer.scale)
            .to({ y: 0.85 }, 250)
            .easing(Easing.Bounce.InOut)
            .yoyo(true)
            .repeat(3);

        // Tween to gradually change scale from original to one to be used when in chest
        let firstCall = true;
        const startWorldScale = { x: 0, y: 0 };
        let progress = 0;
        const updateFinalScaleTween = () => {
            if (firstCall) {
                firstCall = false;
                startWorldScale.x = turnipContainer.scale.x * dynamicData.parentScale;
                startWorldScale.y = turnipContainer.scale.y * dynamicData.parentScale;
                return;
            }
            const newWorldScale = {
                x: MathHelper.lerp(startWorldScale.x, finalScale, progress),
                y: MathHelper.lerp(startWorldScale.y, finalScale, progress),
            };
            turnipContainer.scale.x = newWorldScale.x / dynamicData.parentScale;
            turnipContainer.scale.y = newWorldScale.y / dynamicData.parentScale;
        };
        const final = this.getUpdateTween(p => {
            progress = p;
            updateFinalScaleTween();
        }, flyDurationMs);

        return {
            yoyo: this.generateTween(yoyo),
            final: this.generateTween(final),
            updateFinalScaleTween
        };
    }

    private getPositionTweens(
        positionToUpdate: PIXI.Container['position'],
        dynamicStartPosition: { x: number; y: number },
        dynamicTargetPosition: { x: number; y: number },
        dynamicParentScale: { parentScale: number },
        duration: number
    ) {
        const fly = this.getUpdateTween(progress => {
            const arcHeightFactor = 333 / dynamicParentScale.parentScale;
            const controlPoint1 = {
                x: dynamicStartPosition.x,
                y: dynamicStartPosition.y - arcHeightFactor,
            };
            const controlPoint2 = {
                x: dynamicTargetPosition.x,
                y: dynamicTargetPosition.y - 0.5 * arcHeightFactor,
            };
            const xy = MathHelper.cubicBezier(
                Easing.Sinusoidal.Out(progress),
                dynamicStartPosition,
                controlPoint1,
                controlPoint2,
                dynamicTargetPosition
            );
            positionToUpdate.set(xy.x, xy.y);
        }, duration);
        return {
            fly: this.generateTween(fly),
        };
    }

    private getUpdateTween(
        onUpdate: (progress: number) => void,
        duration: number,
    ) {
        return this.getTween({ progress: 0 })
            .to({ progress: 1 }, duration)
            .onUpdate(t => { onUpdate(t.progress); });
    }

    private *generateTween(tween: Tween): IterableIterator<number> {
        yield* this.startTween(tween);
    }
}
